import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "redux/slices/userSlice";
import { removeUser } from "redux/slices/userSlice";
import {
  removePolicies,
  searchPolicies,
} from "redux/slices/searchPoliciesSlice";
import { getUser } from "service/userservice/getUser";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { searchUsers } from "service/userservice/searchUsers";
import { getImage, isCSR, isCSRAdmin, isFlagEnabled } from "js/utilities";
import { useTrackCSRLoginAsUser } from "logging";
import { convertToTitleCase } from "js/formatting";
import { Trans, useTranslation } from "react-i18next";

import EverlakeLoadingOverlay from "./EverlakeLoadingOverlay";
import FormInputError from "components/General/FormInputError";
import UserInfoLookUpDialog from "./UserInfoLookUpDialog";
import GeneralPurposeDialog from "./GeneralPurposeDialog";
import config from "config/config";

export default function CsrSearchboard({ portal }) {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { csrUser } = useSelector((s) => s.csrUser);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const [formInputError, setFormInputError] = useState();
  const [loading, setLoading] = useState();
  const [showDialog, setShowDialog] = useState(false);
  const [users, setUsers] = useState([]);
  const [policyNumber, setPolicyNumber] = useState();
  const [modalState, setModalState] = useState(false);
  const [errorInfo, seterrorInfo] = useState("");
  const [errorTitle, setErrorTitle] = useState("");
  const trackCSRLoginAsUser = useTrackCSRLoginAsUser();

  async function impersonate(data) {
    // call getUseProfile portal api for agent Id and alternative Id
    let userProfile = await getUser(data.userName, portal).catch((error) => {
      console.error(error);
    });
    dispatch(setUser(userProfile));
    if (userProfile) {
      setLoading(true);
      setFormInputError("");
      dispatch(searchPolicies(userProfile.identityNumber)).then((response) => {
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            response?.payload?.hasDomesticViolencePolicy === true ||
            (response?.payload?.hasOwnerDeathClaimExists === true &&
              response?.payload?.policies?.length === 0)
          ) {
            dispatch(removeUser());
            dispatch(removePolicies());
            setLoading(false);
            setModalState(true);
            setErrorTitle(t("CustomerLogin-domestic-violence-error-title"));
            response?.payload?.hasOwnerDeathClaimExists === true
              ? seterrorInfo(
                  <Trans
                    i18nKey={"CustomerLogin-death-claim-exists-error-message"}
                    components={{
                      link1: (
                        <Link
                          className="text-ui-blue underline"
                          to="/guest/help"
                        ></Link>
                      ),
                    }}
                  />
                )
              : seterrorInfo(
                  t("CustomerLogin-domestic-violence-error-message")
                );
          } else {
            setLoading(false);
            trackCSRLoginAsUser(userProfile);
            navigate("/policies");
          }
        } else {
          setLoading(false);
          setFormInputError(
            "Server was unable to connect, please try again later."
          );
        }
      });
    } else {
      setLoading(false);
      setFormInputError("No user found");
      dispatch(removeUser());
    }
  }
  async function onSubmit(data) {
    setPolicyNumber(data?.userName);
    setLoading(true);
    let usersProfile = await searchUsers(data.userName).catch((error) => {
      setLoading(false);
      setFormInputError(
        "Server was unable to connect, please try again later."
      );
      console.error(error);
    });
    if (usersProfile) {
      if (usersProfile?.length > 1) {
        setUsers(usersProfile);
        setShowDialog(true);
      } else {
        await impersonate(data);
      }
      setLoading(false);
    } else {
      setLoading(false);
      setFormInputError("No user found");
      dispatch(removeUser());
    }
  }

  useBreadcrumb([]);

  return (
    <>
      {csrUser?.userId && (
        <a className="ui-skip-to-content block" href="#mainContent">
          Skip to main content
        </a>
      )}

      <div id="id_csr">
        <div id="id_csrTopBar">
          <div
            id="id_utilityMenu"
            className="ui-utilitybar bg-[color:var(--theme-bgcolor-portal)]"
          >
            <div className="container pt-4">
              <div className="flex justify-between flex-row-reverse items-center lg:grid lg:gap-x-2 lg:grid-cols-[1fr_max-content_max-content] lg:items-center">
                <div className="lg:flex lg:items-center px-0">
                  <img
                    src={getImage("image10.svg")}
                    className="h-5 px-0"
                    alt="NTT Data Logo"
                  />
                </div>
                <div className="flex text-[color:var(--theme-color-portal-title)] font-semibold px-0 border-theme-rule">
                  <div className="lg:flex">
                    <div id="everlakeLogoHeader">
                      <img
                        src={getImage("logo_blue.png")}
                        className="ui-logo-header"
                        alt={`${convertToTitleCase(config.COMPANY_NAME)} Logo`}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex text-[color:var(--theme-color-portal-title)] font-semibold px-0 border-theme-rule">
                  Customer Portal
                </div>
              </div>

              <hr className="mt-4 mb-1" />

              {location.pathname !== "/csradmin" &&
                location.pathname !== "/csr/forms" && (
                  <>
                    <div className="flex justify-between">
                      <div className="flex gap-2">
                        {isFlagEnabled("ENABLE_CSR_ADMIN") &&
                        isCSRAdmin(csrUser) ? (
                          <Link
                            to="/csradmin"
                            className="ui-btn-primary underline"
                          >
                            Admin Page
                          </Link>
                        ) : null}
                        {isCSR(csrUser) || isCSRAdmin(csrUser) ? (
                          <Link
                            to="/csr/forms"
                            className="ui-btn-primary underline"
                          >
                            Forms
                          </Link>
                        ) : null}
                      </div>
                      <button
                        className="ui-btn-hyperlink text-xs"
                        onClick={() => navigate("/")}
                      >
                        Logout CSR
                      </button>
                    </div>
                    <div className="ui-header-search-desktop container">
                      <div className="mx-auto w-3/5 xl:w-1/2">
                        <div className="mx-auto w-3/5 xl:w-1/2 py-4">
                          <p className="font-bold">Customer lookup</p>
                        </div>
                        <EverlakeLoadingOverlay isLoading={loading}>
                          <form
                            className="flex flex-col sm:flex-row"
                            onSubmit={handleSubmit(onSubmit)}
                          >
                            <div className="flex flex-col flex-grow justify-start sm:mr-4">
                              <div className="relative">
                                <input
                                  id="ID_username"
                                  type="text"
                                  placeholder="Enter customer user name or policy-id"
                                  aria-placeholder="Enter customer user name or policy-id"
                                  aria-labelledby="ID_usernameLabel"
                                  aria-describedby="ID_usernameError"
                                  className="border border-ui-gray-medium h-9 w-full p-2 pl-8 rounded"
                                  {...register("userName", {
                                    required: {
                                      value: true,
                                      message:
                                        "Please enter username or policy-id.",
                                    },
                                  })}
                                />
                                <svg
                                  className="ui-icon w-4 h-4 absolute text-theme-action left-0 top-1/2 mt-[-.5rem] ml-2"
                                  focusable="false"
                                  aria-hidden="true"
                                  role="presentation"
                                >
                                  <use href="#ui-icon-search"></use>
                                </svg>
                              </div>
                              <div id="ID_usernameError">
                                {errors.username && (
                                  <div className="ui-input-error">
                                    {errors.username.message}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div>
                              <button className="ui-btn-primary h-9 w-32 mx-auto ui-btn-min-w mt-4 sm:mt-0">
                                Submit
                              </button>
                            </div>
                          </form>
                        </EverlakeLoadingOverlay>
                        {!loading && (
                          <FormInputError errorMessage={formInputError} />
                        )}
                      </div>
                    </div>
                  </>
                )}
            </div>
          </div>
        </div>
      </div>
      <GeneralPurposeDialog
        state={modalState}
        setState={setModalState}
        desc={errorInfo}
        title={errorTitle}
        width="max-w-xl"
      />
      <UserInfoLookUpDialog
        showDialog={showDialog}
        setShowDialog={setShowDialog}
        data={users}
        impersonate={impersonate}
        policyNumber={policyNumber}
      />
    </>
  );
}
